import React, { useEffect, useState } from 'react';
import {
    Alert, AlertTitle, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
    DialogContent, IconButton, Stack, Typography, TextField, alpha,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { ClockIcon, StopCircleIcon, PlayCircleIcon } from '@heroicons/react/24/outline';
import { Capacitor } from '@capacitor/core';
import { getShiftStatus, startShift, endShift, onShiftStatusChange } from '../services/shiftTrackingService';
import { jornadaLocationNotice, jornadaStatusLabel } from '../services/jornadaUiMessages';
import { useToast } from '../ui/ToastProvider';
import JornadaPausasControl from './JornadaPausasControl';
import { useAuth } from '../auth/AuthContext';
import { canRrhh } from '../services/rrhhAccess';

const formatStartTime = (iso) => {
    if (!iso) return '';
    try { return new Intl.DateTimeFormat('es-AR', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso)); }
    catch { return ''; }
};

export default function ShiftTrackingControl({ compact = false }) {
    const toast = useToast();
    const { user } = useAuth();
    const [shift, setShift] = useState(getShiftStatus);
    const [dialog, setDialog] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [descanso, setDescanso] = useState('0');
    const busy = actionLoading || shift.gpsStatus === 'checking';
    const gpsReady = shift.active && shift.gpsStatus === 'ready';
    const statusLabel = jornadaStatusLabel(shift, busy);
    const legacyBreak = shift.schemaVersion !== 2;
    const canAct = canRrhh(user, shift.active ? 'jornadas.finalizar' : 'jornadas.iniciar');
    const canView = canAct || canRrhh(user, 'jornadas.ver_propias') || canRrhh(user, 'jornadas.ver_equipo');
    const descansoInvalido = legacyBreak && (descanso.trim() === '' || !Number.isInteger(Number(descanso)) || Number(descanso) < 0 || Number(descanso) > (shift.durationMinutes || 0));

    useEffect(() => {
        const update = () => setShift(getShiftStatus());
        update();
        const interval = setInterval(update, 30000);
        const unsubscribe = onShiftStatusChange(update);
        return () => { clearInterval(interval); unsubscribe(); };
    }, []);

    const handleStart = async () => {
        if (!canRrhh(user, 'jornadas.iniciar')) return;
        setActionLoading(true);
        try {
            const result = await startShift();
            if (!result.active || result.gpsStatus !== 'ready') return;
            setDialog(null);
            toast.success('Inicio de jornada guardado');
        } catch (error) {
            if (error?.code === 'DATA_USAGE_PERMISSION_REQUIRED') { setDialog(null); return; }
            toast.error(error?.message || 'No se pudo guardar el inicio de jornada.');
        } finally { setActionLoading(false); }
    };

    const handleEnd = async () => {
        if (!canRrhh(user, 'jornadas.finalizar')) return;
        if (descansoInvalido) return;
        setActionLoading(true);
        try {
            await endShift('manual', legacyBreak ? Number(descanso) : 0);
            setDescanso('0');
            setDialog(null);
            toast.info('Cierre de jornada guardado');
        } catch (error) {
            toast.error(error?.message || 'No se pudo guardar el cierre de jornada. Reintentá.');
        } finally { setActionLoading(false); }
    };

    const feedback = shift.gpsError ? <Alert severity="warning" sx={{ mt: 2 }}>
        <AlertTitle>Revisar jornada</AlertTitle>
        {shift.gpsError}
        {shift.active && <Button onClick={handleStart} disabled={busy} sx={{ display: 'block', mt: 1, minHeight: 44 }}>Revisar jornada</Button>}
    </Alert> : shift.active && !gpsReady ? <Alert severity="info" sx={{ mt: 2 }}>
        {busy ? 'Verificando jornada…' : 'La jornada está abierta. Revisá el registro para continuar.'}
        {!busy && <Button onClick={handleStart} sx={{ display: 'block', mt: 1, minHeight: 44 }}>Revisar jornada</Button>}
    </Alert> : null;

    const opened = shift.active
        ? <>Iniciada a las {formatStartTime(shift.startedAt)} ({shift.durationMinutes} min transcurridos)</>
        : 'Iniciá tu jornada laboral al comenzar tus tareas.';

    if (!canView) return null;
    return <>
        {compact ? <button type="button"
            className={shift.active ? 'react-shell-shift-pill' : 'react-shell-shift-pill-inactive'}
            style={{ minHeight: 34 }} disabled={actionLoading}
            onClick={() => setDialog(shift.active ? 'end' : 'start')}
            title={shift.active ? 'Ver o finalizar la jornada laboral' : 'Iniciar jornada laboral'}
            aria-label={shift.active ? `${statusLabel}. Ver o finalizar la jornada laboral` : 'Iniciar jornada laboral'}>
            <ClockIcon className="react-shell-shift-icon" aria-hidden="true" />
            <span className="react-shell-shift-text">{shift.active || busy || shift.gpsError ? statusLabel : 'Iniciar jornada'}</span>
            {shift.active && <span className="react-shell-shift-subtext">· {shift.durationMinutes}m</span>}
        </button> : <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
            <CardContent sx={{ p: { xs: 1.15, sm: 1.5 }, '&:last-child': { pb: { xs: 1.15, sm: 1.5 } } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: (theme) => alpha(gpsReady ? theme.palette.success.main : theme.palette.primary.main, 0.12),
                            color: gpsReady ? 'success.main' : 'primary.main', flexShrink: 0 }}>
                            <ClockIcon style={{ width: 18, height: 18 }} aria-hidden="true" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.75} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.88rem' }}>Jornada laboral</Typography>
                                <Chip label={statusLabel} color={gpsReady ? 'success' : shift.active || shift.gpsError ? 'warning' : 'default'} size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2, fontSize: '0.74rem', lineHeight: 1.3 }}>{opened}</Typography>
                        </Box>
                    </Stack>
                    <Button variant="contained" color={shift.active ? 'error' : 'primary'} disabled={!canAct || actionLoading || (!shift.active && busy)}
                        startIcon={shift.active ? <StopCircleIcon style={{ width: 18, height: 18 }} /> : <PlayCircleIcon style={{ width: 18, height: 18 }} />}
                        onClick={() => setDialog(shift.active ? 'end' : 'start')} sx={{ minHeight: 42, px: 2, fontSize: '0.84rem', textTransform: 'none', borderRadius: '8px', flexShrink: 0 }}>
                        {shift.active ? 'Finalizar jornada' : busy ? 'Verificando jornada…' : 'Iniciar jornada'}
                    </Button>
                </Stack>
                {feedback}
                <JornadaPausasControl shift={shift} />
            </CardContent>
        </Card>}

        <Dialog
            open={dialog !== null}
            onClose={() => !actionLoading && setDialog(null)}
            maxWidth="xs"
            fullWidth
            aria-labelledby="shift-dialog-title"
            aria-describedby="shift-dialog-description"
            slotProps={{ paper: { sx: {
                    m: 2, width: 'calc(100% - 32px)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
                    '& .MuiButton-root': { minHeight: 44, textTransform: 'none', fontWeight: 600 }
                } } }}
        >
            <Box sx={{
                p: 2, gap: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}>
                <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 0 }}>
                    <Box sx={{
                        width: 24, height: 24, flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: dialog === 'start' ? '#059669' : '#dc2626',
                    }}>
                        {dialog === 'start' ? <PlayCircleIcon style={{ width: 24, height: 24 }} /> : <StopCircleIcon style={{ width: 24, height: 24 }} />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography id="shift-dialog-title" variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary', lineHeight: 1.3 }}>
                            {dialog === 'start' ? 'Iniciar jornada laboral' : 'Finalizar jornada laboral'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            {dialog === 'start' ? 'Registro de presencia y horario' : 'Cierre y sincronización del turno'}
                        </Typography>
                    </Box>
                </Stack>
                <IconButton
                    size="small"
                    onClick={() => setDialog(null)}
                    disabled={actionLoading}
                    aria-label="Cerrar ventana"
                    sx={{ color: 'text.secondary', width: 44, height: 44, flexShrink: 0, borderRadius: '8px' }}
                >
                    <CloseRoundedIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Stack spacing={1.5}>
                    <Typography id="shift-dialog-description" variant="body2" sx={{ color: 'text.primary', lineHeight: 1.55 }}>
                        {dialog === 'start'
                            ? jornadaLocationNotice(Capacitor.getPlatform())
                            : 'El cierre queda guardado en el teléfono y se sincroniza con Coordinación al tener conexión.'}
                    </Typography>

                    {dialog === 'start' ? (
                        feedback
                    ) : (
                        <>
                            <Box
                                sx={{
                                    py: 1.5,
                                    borderTop: '1px solid', borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Box component="dl" sx={{ m: 0, display: 'grid', gap: 1.25,
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                                    <Box>
                                        <Typography component="dt" variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                            INICIO REGISTRADO
                                        </Typography>
                                        <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.25, fontWeight: 600, color: 'text.primary' }}>
                                            {opened || 'No disponible'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography component="dt" variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                            CIERRE DE SEGURIDAD
                                        </Typography>
                                        <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.25, fontWeight: 600, color: 'text.primary' }}>
                                            {shift.maxDurationHours} h
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {legacyBreak ? (
                                <TextField
                                    label="Descanso no trabajado (minutos)"
                                    type="number"
                                    value={descanso}
                                    onChange={(event) => setDescanso(event.target.value)}
                                    fullWidth
                                    error={descansoInvalido}
                                    helperText={
                                        descansoInvalido
                                            ? 'Indicá minutos entre 0 y la duración de la jornada.'
                                            : 'Se descuenta del tiempo trabajado. No se resta automáticamente ningún descanso.'
                                    }
                                    inputProps={{ min: 0, max: shift.durationMinutes || 0, step: 1 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            bgcolor: '#ffffff',
                                        }
                                    }}
                                />
                            ) : (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', px: 0.5 }}>
                                    Los descansos se calculan con las pausas fichadas y la política de Coordinación. Una pausa abierta se cierra junto con la jornada.
                                </Typography>
                            )}

                            {feedback}
                        </>
                    )}

                    {compact && <JornadaPausasControl shift={shift} />}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', gap: 1,
                flexWrap: 'wrap', '& > :not(style) ~ :not(style)': { ml: 0 } }}>
                <Button
                    onClick={() => setDialog(null)}
                    disabled={actionLoading}
                    sx={{ color: 'text.secondary', px: 2, borderRadius: '8px' }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    color={dialog === 'start' ? 'primary' : 'error'}
                    onClick={dialog === 'start' ? handleStart : handleEnd}
                    disabled={!canRrhh(user, dialog === 'start' ? 'jornadas.iniciar' : 'jornadas.finalizar') || (dialog === 'start' ? busy : actionLoading || descansoInvalido)}
                    sx={{
                        borderRadius: '8px',
                        px: 2.5, whiteSpace: 'normal',
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none' },
                        ...(dialog === 'start' ? {
                            bgcolor: '#059669',
                            '&:hover': { bgcolor: '#047857' }
                        } : {})
                    }}
                >
                    {dialog === 'start'
                        ? busy ? 'Verificando jornada…' : 'Iniciar jornada'
                        : actionLoading ? 'Finalizando…' : 'Finalizar jornada'}
                </Button>
            </DialogActions>
        </Dialog>
    </>;
}
